// creature-console.ts — client-side render of the encounter-creature CONSOLE
// (the action strip above the summary row), reading from window.FateTide.casterCache.
//
// Replaces the GM-controls + status-effect portions of
// options/tables/creature/creature_console_inc.php:
//   A. GM controls (gated on has_player_permission || has_campaign_permission, both
//      embedded in caster.permissions): edit, hide/reveal, private toggle,
//      kill/resurrect/destroy, hunter-summon dismiss/recall, force-refresh.
//   B. Status section: show-status button (player-permission jumps to the modify form)
//      + the status-effect icon row.
//
// NOT ported here: the pickpocket / trade / train block (C). It depends on the VIEWER's
// current-PC identity (cookie/request), the encounter roster, and is_within_reach
// FOOTPRINT GEOMETRY (both creatures' size, picture aspect ratio, XZ positions) — data
// that needs a live encounter blob to verify. C stays in
// options/tables/creature/creature_console_inc.php (slimmed to just that block).
//
// Console renders only for creatures in an encounter (encounter_creature_id present);
// otherwise it's empty (matches the PHP gate).

import { hasPlayerPermission, hasCampaignPermission } from '../shared/array-types';
import { cardEditQuery } from '../shared/card-edit-publication';
import { paintMarkup } from '../player-character-sheet/paint-markup';
import { statusRunes, statusRuneMountHtml } from '../shared/status-rune';
import type { CachedCaster, ItemLikeRecord } from './types';
import type { SceneObjectAction } from '../encounter-creation-engine/scene-object-action-menu';
import { ftOpenPushEncounterCreatureForm } from './push-encounter-creature-form';
import { getSwarmTemplateForCreature } from '../shared/swarm-templates';

const HUNTER_SUMMON_TYPE_ID = '4E601894-014E-32FB-E138-2A261969B7B2';
const DISMISS_SUMMON_ABILITY_ID = '8C7CDFC0-E251-4CD3-ACA8-70F095746621';
const RECALL_SUMMON_ABILITY_ID = '73AF8EC0-6717-475A-B272-15B2542C91E0';
const HIDDEN_STATUS_ID = '47667406-1B72-9D60-310B-D8CF6BFE397D'; // StatusEffect 'hidden'
const ACTION_BAR_STATUS_INLINE_LIMIT = 10;

function callWindow(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as Record<string, unknown>)[name];
    return typeof fn === 'function' ? (fn as (...values: unknown[]) => unknown)(...args) : undefined;
}

function consoleRecord(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function consoleStatusDef(value: unknown): { items?: unknown; item_layer_types?: unknown; catalog?: unknown } | null {
    const record = consoleRecord(value);
    return record ? {
        items: record.items,
        item_layer_types: record.item_layer_types,
        catalog: record.catalog,
    } : null;
}

async function queryDataWindow(url: string, target: string, payload: Record<string, unknown>): Promise<void> {
    await callWindow('queryData', url, target, payload);
}

function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}
function num(v: unknown): number {
    const n = Number(unwrap(v));
    return Number.isFinite(n) ? n : 0;
}
function str(v: unknown): string {
    return String(unwrap(v) ?? '');
}
/** layer_has twin: is this boolean-ish layer flag present and truthy on the caster? */
function layerTruthy(caster: CachedCaster, layerName: string): boolean {
    const v = unwrap(ftLayerValue(caster, layerName));
    return v !== null && v !== undefined && v !== '' && v !== '0' && v !== 0 && v !== false;
}
function ecArrOf(caster: CachedCaster): Record<string, unknown> {
    const ec = caster.encounter_creatures;
    return (ec && !Array.isArray(ec)) ? (ec as Record<string, unknown>) : {};
}
/** item_field twin: read items.<f> or top-level <f>, [0]-unwrapped. */
function itemField(row: unknown, field: string): string {
    if (!row || typeof row !== 'object') return '';
    const o = row as ItemLikeRecord;
    const raw = o.items?.[field] ?? o[field];
    return String(unwrap(raw) ?? '');
}
/** vitality_of(...).v_current via the computed.vitality bridge. */
function vCurrent(caster: CachedCaster): number {
    return num(ftComputedValue(caster, 'vitality', 'v_current'));
}
/** has_status_effect twin: does any status_effect row carry this catalog item_id? */
function hasStatusEffect(caster: CachedCaster, statusItemId: string): boolean {
    const statuses = ftCreatureStatuses(caster, 'target');
    if (!Array.isArray(statuses)) return false;
    const want = statusItemId.toUpperCase();
    // Lean status rows carry only a status_item_id ref; identity lives on the resolved def
    // (ftCreatureStatuses fills status_arr from the catalog). Read it from the def first,
    // falling back to the row for legacy inline-def rows — same as the status-section loop.
    return statuses.some((st) => {
        const def = consoleRecord(st)?.status_arr;
        const id = itemField(def, 'item_id') || itemField(st, 'item_id');
        return id.toUpperCase() === want;
    });
}

// ── GM action button builders ─────────────────────────────────────────────────
// Each emits ONE self-contained button block, reading its live state straight off the
// caster blob. Shared by the full console GM strip (renderGmControls) and the compact
// encounter-creature ROSTER strip (renderRosterButtons). The optional idPrefix namespaces
// the element ids so the roster strip and the console strip can coexist in the same DOM
// (e.g. my_campaign.php with a creature card open) without their show/hide toggles fighting
// over a shared getElementById target.

// Hide / Reveal (render_hide_encounter_creature_buttons twin; initial state from
// check_target_hidden = the 'hidden' STATUS effect, NOT the private flag).
function gmBtnHideReveal(caster: CachedCaster, ecId: string, idPrefix = ''): string {
    const isHidden = hasStatusEffect(caster, HIDDEN_STATUS_ID);
    const e = escapeHtml(ecId);
    const revealId = `${idPrefix}btn_reveal_creature_${e}`;
    const hideId = `${idPrefix}btn_hide_creature_${e}`;
    const revealStyle = isHidden ? 'display:block;' : 'display:none;';
    const hideStyle = isHidden ? 'display:none;' : 'display:block;';
    return `<div class="pr-2 pb-1">
        <button id="${revealId}" type="button" class="btn btn-light border btn-sm" style="${revealStyle}" data-ft-creature-console-action="reveal" data-ft-ec-id="${e}" data-ft-show-target="${hideId}" data-ft-hide-target="${revealId}">${iconMap('reveal', 25, 25, 'Reveal Creature')}</button>
        <button id="${hideId}" type="button" class="btn btn-dark border btn-sm" style="${hideStyle}" data-ft-creature-console-action="hide" data-ft-ec-id="${e}" data-ft-show-target="${revealId}" data-ft-hide-target="${hideId}">${iconMap('hidden', 25, 25, 'Hide Creature')}</button>
    </div>`;
}

// Private toggle (render_item_private_toggle_buttons twin, item_type=encounter_creature).
function gmBtnPrivateToggle(caster: CachedCaster, ecId: string, idPrefix = ''): string {
    const isPrivate = num(ecArrOf(caster).encounter_creature_private) === 1;
    const e = escapeHtml(ecId);
    const publicId = `${idPrefix}encounter_creature_public_${e}`;
    const privateId = `${idPrefix}encounter_creature_private_${e}`;
    const publicStyle = isPrivate ? 'display:none;' : 'display:block;';
    const privateStyle = isPrivate ? 'display:block;' : 'display:none;';
    return `<div class="pb-1">
        <button id="${publicId}" type="button" class="btn btn-primary btn-sm" style="${publicStyle}" data-ft-creature-console-action="make-private" data-ft-ec-id="${e}" data-ft-show-target="${privateId}" data-ft-hide-target="${publicId}">${iconMap('public')}</button>
        <button id="${privateId}" type="button" class="btn btn-danger btn-sm" style="${privateStyle}" data-ft-creature-console-action="make-public" data-ft-ec-id="${e}" data-ft-show-target="${publicId}" data-ft-hide-target="${privateId}">${iconMap('private')}</button>
    </div>`;
}

// Kill (alive + has vitality) else Resurrect. The optional prefix keeps the reference-only kill id
// unique when the legacy sheet mount and action-bar tools coexist.
function gmBtnKillResurrect(caster: CachedCaster, ecId: string, idPrefix = ''): string {
    const ecArr = ecArrOf(caster);
    const dead = num(ecArr.encounter_creature_dead) === 1;
    const v = vCurrent(caster);
    const e = escapeHtml(ecId);
    if (!dead && v > 0) {
        return `<div class="pr-2 pb-1"><button id="${idPrefix}btn_kill_creature_${e}" class="btn btn-sm btn-danger" type="button" data-ft-creature-console-action="kill" data-ft-ec-id="${e}">${iconMap('kill_creature')}</button></div>`;
    }
    return `<div class="pr-2 pb-1"><button class="btn btn-success btn-sm" type="button" data-ft-creature-console-action="resurrect" data-ft-ec-id="${e}">${iconMap('resurrect_creature')}</button></div>`;
}

// Delete (hard delete from the encounter — renumbers remaining creatures). Toggle-to-confirm
// pattern (render_delete_toggle_buttons twin): the delete button hides itself and reveals the
// confirm button. Confirm calls ftConfirmDeleteEncounterCreature, which fires the delete AND
// syncs the UI (the endpoint emits no output / pushes nothing, so without this the deleted card
// + token would linger until a manual reload).
function gmBtnDelete(ecId: string, idPrefix = ''): string {
    const e = escapeHtml(ecId);
    const delId = `${idPrefix}encounter_creature_delete_${e}`;
    const confirmId = `${idPrefix}encounter_creature_confirm_${e}`;
    return `<div class="pr-2 pb-1">
        <button id="${delId}" type="button" class="btn btn-sm btn-warning" style="display:block;" data-ft-creature-console-action="show-confirm" data-ft-show-target="${confirmId}" data-ft-hide-target="${delId}">${iconMap('delete_encounter_creature')}</button>
        <button id="${confirmId}" type="button" class="btn btn-sm btn-danger" style="display:none;" data-ft-creature-console-action="confirm-delete" data-ft-ec-id="${e}">${iconMap('confirm')}</button>
    </div>`;
}

interface GmControlRenderOptions {
    includeActAs?: boolean;
    idPrefix?: string;
    actionBar?: boolean;
}

function renderGmControls(caster: CachedCaster, ecId: string, options: GmControlRenderOptions = {}): string {
    const ecArr = ecArrOf(caster);
    const isPrivate = num(ecArr.encounter_creature_private) === 1; // drives the summon dismiss/recall pick below
    const includeActAs = options.includeActAs !== false;
    const idPrefix = options.idPrefix ?? '';

    // ACT AS THIS CREATURE control state (the button itself renders on its own row BELOW the icon
    // strip — see end of function). Reflects who you're currently acting as. [UI tidy 2026-06-22]
    const activeCasterId = String(
        window.ftGetActiveCasterId?.() ?? '',
    ).toUpperCase();
    const isActiveCaster = activeCasterId !== '' && activeCasterId === ecId.toUpperCase();

    // Icon action strip (edit / hide / private / kill / destroy / delete / force-refresh / image).
    let html = `<div class="mb-2 d-flex flex-wrap align-items-center justify-content-start${options.actionBar ? ' ft-actionbar-gm-tools__strip' : ''}">`;

    // Edit encounter creature
    // Edit opens the Modify Creature form CLIENT-SIDE from the cached creature node (the PHP form
    // form_modify_encounter_creature_inc.php is retired) — openEditEncounterCreatureForm handles the
    // forceOpenSideLog + Forms-tab activation + render + hydration tail itself.
    html += `<div class="pb-1"><button id="${idPrefix}btn_edit_encounter_creature_${escapeHtml(ecId)}" class="btn btn-sm btn-info" type="button" data-ft-creature-console-action="edit" data-ft-ec-id="${escapeHtml(ecId)}">${iconMap('edit_encounter_creature')}</button></div>`;

    html += '<div class="vr mx-2"></div>';
    html += gmBtnHideReveal(caster, ecId, idPrefix);
    html += gmBtnPrivateToggle(caster, ecId, idPrefix);

    html += '<div class="vr mx-2"></div>';
    html += gmBtnKillResurrect(caster, ecId, idPrefix);

    // Destroy
    html += `<div class="pr-2 pb-1"><button id="${idPrefix}btn_destroy_creature_${escapeHtml(ecId)}" class="btn btn-sm btn-danger" type="button" data-ft-creature-console-action="destroy" data-ft-ec-id="${escapeHtml(ecId)}">${iconMap('destroy_creature')}</button></div>`;

    html += '<div class="vr mx-2"></div>';
    html += gmBtnDelete(ecId, idPrefix);

    // Hunter summon dismiss / recall
    const controllerId = str(ecArr.controller_encounter_creature_id);
    const summonType = str(ecArr.summon_type);
    if (controllerId !== '' && summonType === HUNTER_SUMMON_TYPE_ID) {
        html += '<div class="vr mx-2"></div>';
        if (!isPrivate) {
            html += `<div class="pr-2 pb-1"><button id="btn_dismiss_summon_${escapeHtml(ecId)}" type="button" class="btn btn-sm btn-danger" data-ft-creature-console-action="use-ability" data-ft-ability-id="${DISMISS_SUMMON_ABILITY_ID}">${iconMap('dismiss_summon')}</button></div>`;
        } else {
            html += `<div class="pr-2 pb-1"><button id="btn_recall_summon_${escapeHtml(ecId)}" type="button" class="btn btn-sm btn-danger" data-ft-creature-console-action="use-ability" data-ft-ability-id="${RECALL_SUMMON_ABILITY_ID}">${iconMap('recall_summon')}</button></div>`;
        }
    }

    // Force reload (the card-picture toggle that used to sit beside it was removed 2026-09-02 —
    // see template.js: one click set a 9000-day cookie that blanked the portrait everywhere).
    html += `<div class="pr-2 pb-1"><button id="${idPrefix}btn_force_refresh_sheet" type="button" data-ft-creature-console-action="force-refresh" data-ft-ec-id="${escapeHtml(ecId)}" class="btn btn-sm btn-dark border">${iconMap('force_refresh_caster')}</button></div>`;

    html += '</div>';

    // ACT AS THIS CREATURE — the explicit puppet control, on its own row BELOW the icon strip
    // (UI tidy 2026-06-22). The deliberate way a GM takes control of a creature (abilities / stances /
    // turn); sibling of summon-tabbing (setCasterSummon). Shows "Acting as" (disabled) when active.
    if (includeActAs) {
        if (isActiveCaster) {
            html += `<div class="mb-2"><button type="button" class="btn btn-sm btn-success" disabled title="You are currently acting as this creature">Acting as this creature</button></div>`;
        } else {
            html += `<div class="mb-2"><button type="button" class="btn btn-sm btn-outline-success" data-ft-creature-console-action="act-as" data-ft-ec-id="${escapeHtml(ecId)}" title="Take control of this creature — use its abilities, stances and turn">Act as this creature</button></div>`;
        }
    }

    // GM / dev quick-open for merchant & trainer NPCs. The player console (creature_console_inc.php)
    // only enables Trade/Train when the buyer PC is within reach on the map; this GM-side shortcut
    // skips that geometry, opening the panel with the PC you're currently controlling (ftGetActiveCasterId)
    // as the buyer/student. GM-gated already (renderGmControls renders only with player/campaign permission).
    const isMerchant = layerTruthy(caster, 'merchant');
    const isTrainer = layerTruthy(caster, 'trainer');
    if (isMerchant || isTrainer) {
        const e = escapeHtml(ecId);
        html += '<div class="mb-2 d-flex flex-wrap align-items-center gap-1">';
        if (isMerchant) {
            html += `<button type="button" class="btn btn-sm btn-outline-info d-inline-flex align-items-center gap-1" data-ft-creature-console-action="gm-trade" data-ft-ec-id="${e}" title="Open the trade window as the character you're controlling (GM — ignores map reach)">${iconMap('merchant', 20, 20, false)}<span>Trade</span></button>`;
        }
        if (isTrainer) {
            html += `<button type="button" class="btn btn-sm btn-outline-info d-inline-flex align-items-center gap-1" data-ft-creature-console-action="gm-train" data-ft-ec-id="${e}" title="Open the training window as the character you're controlling (GM — ignores map reach)">${iconMap('trainer', 20, 20, false)}<span>Train</span></button>`;
        }
        html += '</div>';
    }
    return html;
}

// ── GM actions shared by the console strip and the FT3D creature radial ─────────

async function setEncounterCreatureHidden(ecId: string, hidden: boolean): Promise<'handled' | 'refused'> {
    const bridge = (window as Window & {
        ftTryCommitCreatureVisibility?: (id: string, hidden: boolean) => Promise<'handled' | 'refused'>;
    }).ftTryCommitCreatureVisibility;
    if (typeof bridge !== 'function') {
        displayWarning('Creature visibility is unavailable on this page — reload and try again.');
        return 'refused';
    }
    return bridge(ecId, hidden);
}

async function setEncounterCreaturePrivacy(ecId: string, privateActor: boolean): Promise<void> {
    // Paint the local transition immediately; the saved scalar travels on browser truth.
    window.dispatchEvent(new CustomEvent('ft:encounter-creature-privacy-preview', {
        detail: { entityId: ecId, private: privateActor },
    }));
    try {
        await cardEditQuery(
            `${BASE_URL}options/db_queries/update/update_privacy_inc.php`,
            'div_sidelog_warning',
            {
                item_type: 'encounter_creature',
                encounter_creature_id: ecId,
                encounter_creature_private: privateActor ? '1' : '0',
            },
        );
    } catch (error) {
        window.dispatchEvent(new CustomEvent('ft:encounter-creature-privacy-preview', {
            detail: { entityId: ecId, private: !privateActor, rollback: true },
        }));
        throw error;
    }
    // Confirm the saved intent after the acting browser registered its delivery.
    window.dispatchEvent(new CustomEvent('ft:encounter-creature-privacy-committed', {
        detail: { entityId: ecId, private: privateActor },
    }));
}

async function openCreatureStatusManager(ecId: string): Promise<void> {
    callWindow('forceOpenSideLog');
    (document.getElementById('pills_sidelog_form_tab') as HTMLElement | null)?.click();
    if (typeof window.renderCreatureStatusForm === 'function') {
        callWindow('renderCreatureStatusForm', ecId);
        return;
    }
    await queryDataWindow(
        `${BASE_URL}options/tools/statuses/forms/form_modify_creature_status_effects_inc.php`,
        'div_refresh_sidelog_form',
        { caster_id: ecId },
    );
}

async function handleCreatureConsoleAction(el: HTMLElement): Promise<void> {
    const action = el.dataset.ftCreatureConsoleAction || '';
    const ecId = el.dataset.ftEcId || '';
    const hideTarget = el.dataset.ftHideTarget || '';
    const showTarget = el.dataset.ftShowTarget || '';
    const hide = () => { if (hideTarget) { const target = document.getElementById(hideTarget); if (target) target.style.display = 'none'; } };
    const show = () => { if (showTarget) { const target = document.getElementById(showTarget); if (target) target.style.display = 'block'; } };

    if (action === 'show-confirm') {
        hide();
        show();
        return;
    }
    if (action === 'confirm-delete' && ecId) {
        await ftConfirmDeleteEncounterCreature(ecId);
        return;
    }
    if (action === 'reveal' || action === 'hide') {
        if (await setEncounterCreatureHidden(ecId, action === 'hide') !== 'handled') return;
        hide();
        show();
        return;
    }
    if (action === 'make-private' || action === 'make-public') {
        el.setAttribute('disabled', 'disabled');
        try {
            await setEncounterCreaturePrivacy(ecId, action === 'make-private');
        } catch (error) {
            el.removeAttribute('disabled');
            throw error;
        }
        hide();
        show();
        return;
    }

    if (action === 'kill') callWindow('killCreature', ecId);
    if (action === 'resurrect') callWindow('resurrectCreature', ecId);
    if (action === 'edit') callWindow('openEditEncounterCreatureForm', ecId);
    if (action === 'destroy') callWindow('destroyCreature', ecId);
    if (action === 'use-ability') callWindow('useAbility', el.dataset.ftAbilityId || '');
    if (action === 'force-refresh') callWindow('forceCasterArrReset', ecId);
    if (action === 'act-as') callWindow('ftActAsCreature', ecId);
    if (action === 'gm-trade') callWindow('ftGmOpenTrade', ecId);
    if (action === 'gm-train') callWindow('ftGmOpenTrain', ecId);
    if (action === 'status-manage') await openCreatureStatusManager(ecId);
    if (action === 'status-view') callWindow('showStatusEffects', ecId);
    if (action === 'open-item-info') callWindow('openItemInfo', el.dataset.ftItemId || '');
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-creature-console-action]')
        : null;
    if (!el) return;
    event.preventDefault();
    void handleCreatureConsoleAction(el);
});

/**
 * Confirm-delete handler for the GM console's delete toggle. Fires the hard-delete endpoint with
 * campaign/encounter context (so the server logs it in active campaign play — adventure-editing
 * deletes post no campaign and stay silent).
 *
 * The token + canonical encounter row are removed by the WEBSOCKET push the endpoint now emits
 * (process_affected_ids → encounter_update_delta with a pruned ec_manifest) — we do NOT rebuild the
 * XZ widget or the roster report here. This handler only does the surgical, viewer-LOCAL cleanup the
 * WS can't: clear the active caster if it was this creature, drop the GM's own open card/side-info,
 * remove the editor roster row, and splice the modify_encounter id list.
 */
export async function ftConfirmDeleteEncounterCreature(ecId: string): Promise<void> {
    const id = String(ecId || '');
    if (id === '') return;
    const remove = (window as Window & { ftTryDeleteCreature22?: (id: string) => Promise<'handled' | 'refused'> }).ftTryDeleteCreature22;
    if (!remove || await remove(id) !== 'handled') return;

    // ── post-delete UI sync (twin of the report-edit async_script) ──
    // If the deleted creature was the active caster, clear it.
    try {
        const activeId = String(window.ftGetActiveCasterId?.() ?? '');
        if (activeId && activeId.toUpperCase() === id.toUpperCase()
            && typeof window.clearCaster === 'function') {
            window.clearCaster();
        }
    } catch (e) { /* non-fatal */ }

    // Drop the GM's own open card (live card + side-info both carry data-ft-caster-id) plus its
    // editor roster row (div_encounter_creature_id_card_<id>). The roster report is NOT WS-managed,
    // so this surgical removal stands in for the old element_to_remove_after; the live encounter
    // token is handled by the server's encounter_update_delta, not here.
    try {
        document.querySelectorAll(`[data-ft-caster-id="${id}"]`).forEach((el) => el.remove());
        document.getElementById(`div_encounter_creature_id_card_${id}`)?.remove();
        document.getElementById(`div_encounter_lair_id_card_${id}`)?.remove();
    } catch (e) { /* non-fatal */ }

    if (typeof window.buildEncounterLairsReport === 'function') {
        try { await window.buildEncounterLairsReport(); } catch (e) { /* non-fatal */ }
    }

    // Keep the modify_encounter editor's id list in sync — the old roster delete async_script did
    // this splice inline. No-op when the element is absent (e.g. my_campaign.php, which has no
    // modify_encounter_encounter_creature_ids blob).
    try {
        const idsEl = document.getElementById('modify_encounter_encounter_creature_ids');
        if (idsEl) {
            const ids = JSON.parse(idsEl.textContent || '[]');
            if (Array.isArray(ids)) {
                const ix = ids.findIndex((x) => String(x).toUpperCase() === id.toUpperCase());
                if (ix >= 0) {
                    ids.splice(ix, 1);
                    idsEl.textContent = JSON.stringify(ids);
                }
            }
        }
    } catch (e) { /* non-fatal */ }

    // NOTE: deliberately NO buildEncounterCreaturesReport / the retired map initializer here. The token
    // and canonical row are removed by the server's encounter_update_delta (pruned ec_manifest);
    // rebuilding the whole XZ widget / roster on every delete is exactly the full refresh we avoid.
}

// ── FT3D creature radial: the console GM strip as a "GM Tools" submenu ─────────
// Twin of object-card/scene-radial.ts's inventory GM Tools. The scene iframe calls
// window.parent.ftCreatureRadialActions when a creature's wheel opens.

const noRadialAction = (): void => {};

/** Cached caster for a radial, seeded from the loaded encounter blob when the sheet never opened. */
function radialCaster(ecId: string): CachedCaster | undefined {
    const cache = window.FateTide?.casterCache;
    if (!cache?.get(ecId)) callWindow('ftSeedCasterFromEncounterCache', ecId);
    return cache?.get(ecId) as CachedCaster | undefined;
}

function reportRadialFailure(label: string): (error: unknown) => void {
    return (error) => console.error(`creature radial: ${label} failed`, error);
}

function activeCasterIs(ecId: string): boolean {
    return String(window.ftGetActiveCasterId?.() ?? '').toUpperCase() === ecId.toUpperCase();
}

function openSideInfo(renderer: string, ecId: string): void {
    callWindow('forceOpenSideInfoPanel');
    callWindow(renderer, ecId);
}

/** +1 luck (capped at fortune) — the GM window's Award Luck, scoped to this one creature. */
function awardLuck(ecId: string): void {
    const context = consoleRecord(callWindow('getSidelogContext'));
    if (!context) return;
    // The award-luck truth seam (websocket/resource-truth.ts ftAwardLuck): the loader gets the row, one commit, words on a refusal —
    // never a legacy post (N6 2026-10-01: gm_award_luck_inc.php is gone).
    const award = (window as unknown as { ftAwardLuck?: (input: { targetIds: string[]; encounterId?: string; rollPrivate?: number }) => Promise<unknown> }).ftAwardLuck;
    if (typeof award !== 'function') { displayWarning('Award Luck is not available on this page — reload and try again.'); return; }
    void Promise.resolve(award({ targetIds: [ecId], encounterId: String(context.encounter_id ?? ''), rollPrivate: Number(context.roll_private ?? 0) || 0 }))
        .catch(reportRadialFailure('award luck'));
}

/** The health panel's GM controls. Set/adjust need a typed amount, so that one opens the panel. */
/**
 * GM reinforcement of a living swarm (handoff §1; owner 2026-09-24: through the radial). Members come back up to the
 * template's starting count — the compiled stamps swarm_members / swarm_members_start (ft_swarm_compile_live_state).
 */
function creatureSwarmReinforce(ecId: string, caster: CachedCaster | null | undefined): SceneObjectAction | null {
    if (!caster || !getSwarmTemplateForCreature(caster as unknown as Parameters<typeof getSwarmTemplateForCreature>[0])) return null;
    const ecArr = ecArrOf(caster);
    const start = num(ecArr.swarm_members_start);
    const members = num(ecArr.swarm_members);
    const dead = num(ecArr.encounter_creature_dead) === 1;
    const full = start > 0 && members >= start;
    const reason = dead ? 'Resurrect the swarm first' : full ? 'Already at full strength' : `${members} of ${start} members`;
    return { label: 'Reinforce', icon: 'swarm_width_lanes', enabled: !dead && !full, reason, run: noRadialAction,
        children: () => [
            { label: '+1 Member', icon: 'add_creature', run: () => { callWindow('reinforceSwarm', ecId, 1); } },
            { label: 'Full Strength', icon: 'summon_creature', run: () => { callWindow('reinforceSwarm', ecId, 'full'); } },
        ] };
}

function creatureVitals(ecId: string, caster?: CachedCaster | null): SceneObjectAction[] {
    const reinforce = creatureSwarmReinforce(ecId, caster);
    return [
        { label: 'Heal to Max', icon: 'heal_to_v_max', run: () => {
            // The resource truth seam (websocket/resource-truth.ts) — never a legacy post (N6 2026-10-01).
            const adjust = (window as unknown as { ftResourceAdjust?: (k: string, id: string, input: null) => Promise<unknown> }).ftResourceAdjust;
            if (typeof adjust !== 'function') { displayWarning('Heal to Max is not available on this page — reload and try again.'); return; }
            void Promise.resolve(adjust('heal_to_max', ecId, null)).catch(reportRadialFailure('heal to max'));
        } },
        { label: 'Adjust Vitality', icon: 'vitality', reason: 'Opens the vitality panel: set current, delta, temp and max',
            run: () => openSideInfo('renderCreatureHealthInfoPanel', ecId) },
        { label: 'Award Luck', icon: 'award_luck', reason: '+1 luck, up to the creature\'s fortune', run: () => awardLuck(ecId) },
        ...(reinforce ? [reinforce] : []),
    ];
}

/** Merchant / trainer shortcuts and the hunter-summon controls — present only when they apply. */
function creatureNpcTools(ecId: string, caster: CachedCaster): SceneObjectAction[] {
    const tools: SceneObjectAction[] = [];
    if (layerTruthy(caster, 'merchant')) {
        tools.push({ label: 'GM Trade', icon: 'merchant', reason: 'Trade as the character you control — ignores map reach',
            run: () => { callWindow('ftGmOpenTrade', ecId); } });
    }
    if (layerTruthy(caster, 'trainer')) {
        tools.push({ label: 'GM Train', icon: 'trainer', reason: 'Train as the character you control — ignores map reach',
            run: () => { callWindow('ftGmOpenTrain', ecId); } });
    }
    const ecArr = ecArrOf(caster);
    if (str(ecArr.controller_encounter_creature_id) !== '' && str(ecArr.summon_type) === HUNTER_SUMMON_TYPE_ID) {
        // Janus casts these AS the summon and charges its controller (summon_control_rolls.php), so the
        // summon must be the active caster — never fire them for whoever else is controlled.
        const recall = num(ecArr.encounter_creature_private) === 1;
        const acting = activeCasterIs(ecId);
        tools.push({ label: recall ? 'Recall Summon' : 'Dismiss Summon', icon: recall ? 'recall_summon' : 'dismiss_summon',
            enabled: acting, reason: acting ? null : 'Control this summon first — its controller pays 3 AP',
            run: () => { if (activeCasterIs(ecId)) callWindow('useAbility', recall ? RECALL_SUMMON_ABILITY_ID : DISMISS_SUMMON_ABILITY_ID); } });
    }
    return tools;
}

/** Built when the submenu opens, so each toggle offers the opposite of the creature's current state.
 * The FT3D host page keeps no full encounter blob, so a creature whose sheet never opened is fetched
 * here (the wheel shows a Loading tab meanwhile). */
async function creatureGmTools(ecId: string): Promise<SceneObjectAction[]> {
    const caster = radialCaster(ecId) ?? await resolveConsoleCaster(ecId);
    const actions: SceneObjectAction[] = [
        { label: 'Edit', icon: 'edit_encounter_creature', run: () => { callWindow('openEditEncounterCreatureForm', ecId); } },
    ];
    if (caster) {
        const ecArr = ecArrOf(caster);
        const hidden = hasStatusEffect(caster, HIDDEN_STATUS_ID);
        actions.push({ label: hidden ? 'Reveal' : 'Hide', icon: hidden ? 'reveal' : 'hidden',
            run: () => { void setEncounterCreatureHidden(ecId, !hidden).catch(reportRadialFailure('hide/reveal')); } });
        const isPrivate = num(ecArr.encounter_creature_private) === 1;
        actions.push({ label: isPrivate ? 'Make Public' : 'Make Private', icon: isPrivate ? 'public' : 'private',
            run: () => { void setEncounterCreaturePrivacy(ecId, !isPrivate).catch(reportRadialFailure('privacy')); } });
        // Kill stays one click like the console strip (Resurrect undoes it); Destroy and Delete confirm.
        actions.push(num(ecArr.encounter_creature_dead) !== 1 && vCurrent(caster) > 0
            ? { label: 'Kill', icon: 'kill_creature', run: () => { callWindow('killCreature', ecId); } }
            : { label: 'Resurrect', icon: 'resurrect_creature', run: () => { callWindow('resurrectCreature', ecId); } });
    } else {
        actions.push({ label: 'State unavailable', icon: 'force_refresh_caster', enabled: false,
            reason: 'This creature could not be loaded — hide, privacy and kill need its current state', run: noRadialAction });
    }
    actions.push({ label: 'Vitals', icon: 'vitality', children: () => creatureVitals(ecId, caster), run: noRadialAction });
    if (caster && num(ecArrOf(caster).encounter_creature_dead) !== 1) {
        actions.push({ label: 'Flee', icon: 'fleeing', reason: 'Leave the battlefield alive', run: noRadialAction,
            children: () => [{ label: 'Confirm Flee', icon: 'fleeing', run: () => { callWindow('fleeCreature', ecId); } }] });
    }
    actions.push({ label: 'Destroy', icon: 'destroy_creature', run: noRadialAction,
        children: () => [{ label: 'Confirm Destroy', icon: 'confirm', run: () => { callWindow('destroyCreature', ecId); } }] });
    actions.push({ label: 'Delete', icon: 'delete_encounter_creature', run: noRadialAction,
        children: () => [{ label: 'Confirm Delete', icon: 'confirm',
            run: () => { void ftConfirmDeleteEncounterCreature(ecId); } }] });
    actions.push({ label: 'More', icon: 'info', run: noRadialAction, children: () => [
        { label: 'Image Gallery', icon: 'modify_picture_gallery', run: () => {
            window.location.assign(`${BASE_URL}tools/media/media_manager.php?type=encounter_creature&id=${encodeURIComponent(ecId)}`);
        } },
        { label: 'Refresh', icon: 'force_refresh_caster', run: () => { callWindow('forceCasterArrReset', ecId); } },
    ] });
    // Wheel capacity: at most 10 tabs plus Back, so the conditional NPC tools share one submenu.
    if (caster) {
        const npc = creatureNpcTools(ecId, caster);
        if (npc.length) actions.push({ label: 'NPC', icon: npc[0]?.icon, children: () => creatureNpcTools(ecId, caster), run: noRadialAction });
    }
    return actions;
}

/**
 * Hub art for a creature wheel, read from the LIVE caster cache at open time (the encounter truth the
 * websocket refreshes) through the canonical picture walk — so a shapeshift, a dead/rubble swap or a
 * new portrait shows on the next wheel rather than the art the 3D projection snapshotted when the
 * scene was built (owner 2026-09-15). A cold row simply yields null and the scene keeps its own art.
 */
export function ftCreatureRadialThumbnail(ecId: string): { imageUrl: string | null } {
    const id = String(ecId || '').trim();
    const caster = id === '' ? undefined : radialCaster(id);
    const picker = (window as Window & {
        selectCreaturePicture?: (row: unknown) => { picture_url?: unknown; pic_url?: unknown } | null;
    }).selectCreaturePicture;
    const picture = caster && picker ? picker(caster) : null;
    const url = String(picture?.picture_url ?? picture?.pic_url ?? '');
    return { imageUrl: /^https?:\/\//i.test(url) ? url : null };
}

/**
 * Wheel entries for an encounter creature. `canManage` is the scene's own campaign-manage flag: a
 * cached row whose `permissions` a viewer-agnostic broadcast stripped must not hide the tools from
 * the real GM (see resolveConsoleCaster). Player permission (a player's own PC) mirrors the console.
 * The endpoints re-check authority server-side.
 */
export function ftCreatureRadialActions(ecId: string, options: { canManage?: boolean; canPushToEncounter?: boolean } = {}): SceneObjectAction[] {
    const id = String(ecId || '').trim();
    if (id === '') return [];
    const caster = radialCaster(id);
    // GM Tools is the campaign manager's alone (owner 2026-09-11) — a player's own PC keeps its console
    // strip on the sheet, but the wheel's GM submenu does not mirror it.
    const manager = options.canManage === true || (!!caster && hasCampaignPermission(caster));
    const statusEditor = manager || (!!caster && hasPlayerPermission(caster));
    // Every viewer gets Status Effects (the inventory wheel's slot): the GM and a PC's owner get the
    // modify form, others the read-only list, which never includes private statuses.
    const actions: SceneObjectAction[] = [{ label: 'Status Effects', icon: 'show_status_effects', slot: -2, run: () => {
        if (statusEditor) void openCreatureStatusManager(id).catch(reportRadialFailure('status effects'));
        else callWindow('showStatusEffects', id);
    } }];
    if (!manager) return actions;
    // Warm a missing row now so GM Tools usually opens without a Loading tab.
    if (!caster) void resolveConsoleCaster(id);
    // Push to Encounter is the campaign GM's alone (owner 2026-09-11) — a player's own PC travels by the map.
    if (options.canManage === true) {
        actions.push({ label: 'Push to Encounter', icon: 'go_to_encounter',
            enabled: options.canPushToEncounter !== false,
            reason: options.canPushToEncounter === false ? 'Requires a campaign-owned encounter to choose a destination' : null,
            run: () => { void ftOpenPushEncounterCreatureForm(id).catch(reportRadialFailure('push to encounter')); } });
    }
    actions.push({ label: 'GM Tools', icon: 'gm', children: () => creatureGmTools(id), run: noRadialAction });
    return actions;
}

function renderStatusSection(caster: CachedCaster, ecId: string): string {
    // GM / owner sees every status (incl. private); a plain viewer sees only public ones (the
    // private-status toggle is GM/owner-only — see the filter in the status row loop below).
    const permitted = hasPlayerPermission(caster) || hasCampaignPermission(caster);

    // pt-3 so the status row isn't flush against the console tab pills above it (the panel's own
    // breathing room — both the PHP and the cache-rendered mount rely on this, not on the pane wrapper).
    let html = '<div class="mb-2 pt-3 d-flex flex-wrap align-items-center justify-content-start">';

    // Show status effects button — player permission jumps straight to the modify form.
    html += '<div class="pr-1 pb-1">';
    if (hasPlayerPermission(caster)) {
        html += `<button id="btn_show_status_effects_${escapeHtml(ecId)}" class="btn btn-sm btn-light" type="button" data-ft-creature-console-action="status-manage" data-ft-ec-id="${escapeHtml(ecId)}">${iconMap('show_status_effects')}</button>`;
    } else {
        html += `<button id="btn_show_status_effects_${escapeHtml(ecId)}" class="btn btn-sm btn-light" type="button" data-ft-creature-console-action="status-view" data-ft-ec-id="${escapeHtml(ecId)}">${iconMap('show_status_effects')}</button>`;
    }
    html += '</div>';

    html += '<div class="vr mx-2"></div>';

    const statuses = ftCreatureStatuses(caster, 'target');
    if (Array.isArray(statuses)) {
        for (const st of statuses) {
            // Private statuses are GM/owner-only — never surface them to a viewer without permission
            // (mirrors the token / persistent-AOE private filter; the toggle lives in the modify form).
            const status = consoleRecord(st);
            const rawPrivate = status?.status_private;
            if (!permitted && String((Array.isArray(rawPrivate) ? rawPrivate[0] : rawPrivate) ?? '') === '1') continue;
            // The resolved def rides under status_arr (ftCreatureStatuses fills it from the catalog ref);
            // read identity from it, falling back to the row for legacy inline-def rows.
            const def = consoleStatusDef(status?.status_arr);
            const id = itemField(def, 'item_id') || itemField(st, 'item_id');
            if (id === '') continue;
            const name = itemField(def, 'item_name') || itemField(st, 'item_name');
            // Click opens the status INSTANCE card (rendered from the blob via ftRenderStatusInstanceFromCache),
            // so the chip must pass the instance id (status_id), NOT the template id used for the icon/rune.
            // Fall back to the template id for any legacy row missing a status_id.
            const statusInstanceId = itemField(st, 'status_id') || id;
            if (statusRunes(id, def)) {
                // Non-standard status (from an ability/spell/curse → no registered icon): render its L0
                // rune sigil instead of iconMap's "?". The <button> wrapper makes the footer rune scanner
                // skip its openRuneInfo click-wiring (it skips mounts inside a <button>), keeping the same
                // openItemInfo click target as the standard PNG statuses beside it.
                html += `<button type="button" class="btn p-0 border-0 bg-transparent shadow-none lh-1 align-middle pr-1 cursor-pointer" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${escapeHtml(name)}" data-ft-creature-console-action="open-item-info" data-ft-item-id="${escapeHtml(statusInstanceId)}">${statusRuneMountHtml(def ?? {})}</button>`;
            } else {
                html += `<div class="pr-1 cursor-pointer" role="button" data-ft-creature-console-action="open-item-info" data-ft-item-id="${escapeHtml(statusInstanceId)}">${iconMap(id, 25, 25, name)}</div>`;
            }
        }
    }

    html += '</div>';
    return html;
}

/**
 * Bar-styled status cluster for the ACTION BAR (player-character-sheet). Reuses the SAME data + click
 * behaviour as renderStatusSection — a far-left manage/edit button (bordered, reliable click target;
 * owner → modify form in the sidelog, viewer → showStatusEffects side-info) followed by the floating
 * status icons (standard PNG via iconMap, or the L0 rune sigil for ability/spell-granted statuses),
 * each opening its status instance card. Private statuses are filtered for unpermitted viewers. Returns
 * '' when the caster isn't an encounter creature (statuses are an encounter concept) — so a standalone
 * item.php PC bar shows none. Sync. The action bar passes the exact snapshot it is painting so one
 * render pass reads ONE coherent row (statuses cannot lag the AP/HP it sits beside by a websocket
 * frame); the casterCache read is only the fallback for an independent caller. Snapshot ownership
 * only — status keys, families, counts and badge meaning are unchanged (docs/STATUS_STACKS.md).
 */
export function renderActionBarStatusCluster(casterId: string, casterSnapshot?: CachedCaster): string {
    const caster = casterSnapshot
        ?? (window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined);
    if (!caster) return '';
    const ecId = str(ecArrOf(caster).encounter_creature_id);
    if (ecId === '') return '';
    const permitted = hasPlayerPermission(caster) || hasCampaignPermission(caster);

    const editBtn = hasPlayerPermission(caster)
        ? `<button type="button" class="ft-action-status__edit" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Manage status effects" data-ft-creature-console-action="status-manage" data-ft-ec-id="${escapeHtml(ecId)}">${iconMap('show_status_effects', 20, 20, false)}</button>`
        : `<button type="button" class="ft-action-status__edit" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Status effects" data-ft-creature-console-action="status-view" data-ft-ec-id="${escapeHtml(ecId)}">${iconMap('show_status_effects', 20, 20, false)}</button>`;

    let icons = '';
    let iconCount = 0;
    const statuses = ftCreatureStatuses(caster, 'target');
    if (Array.isArray(statuses)) {
        for (const st of statuses) {
            const status = consoleRecord(st);
            const rawPrivate = status?.status_private;
            if (!permitted && String((Array.isArray(rawPrivate) ? rawPrivate[0] : rawPrivate) ?? '') === '1') continue;
            const def = consoleStatusDef(status?.status_arr);
            const id = itemField(def, 'item_id') || itemField(st, 'item_id');
            if (id === '') continue;
            const name = itemField(def, 'item_name') || itemField(st, 'item_name');
            const statusInstanceId = itemField(st, 'status_id') || id;
            const overflowClass = iconCount >= ACTION_BAR_STATUS_INLINE_LIMIT ? ' ft-action-status__icon--overflow' : '';
            if (statusRunes(id, def)) {
                icons += `<button type="button" class="ft-action-status__icon ft-action-status__icon--rune${overflowClass}" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${escapeHtml(name)}" data-ft-creature-console-action="open-item-info" data-ft-item-id="${escapeHtml(statusInstanceId)}">${statusRuneMountHtml(def ?? {})}</button>`;
            } else {
                icons += `<span class="ft-action-status__icon${overflowClass}" role="button" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${escapeHtml(name)}" data-ft-creature-console-action="open-item-info" data-ft-item-id="${escapeHtml(statusInstanceId)}">${iconMap(id, 22, 22, false)}</span>`;
            }
            iconCount++;
        }
    }
    const hiddenCount = Math.max(0, iconCount - ACTION_BAR_STATUS_INLINE_LIMIT);
    if (hiddenCount > 0) {
        const label = `Show ${hiddenCount} more status effect${hiddenCount === 1 ? '' : 's'}`;
        icons += `<button type="button" class="ft-action-status__more" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}" data-ft-creature-console-action="status-view" data-ft-ec-id="${escapeHtml(ecId)}">+${hiddenCount}</button>`;
    }
    return `<div class="ft-action-status">${editBtn}${icons}</div>`;
}

/** Permission-gated GM controls for the creature currently bound to the FT3D action bar.
 * The action bar already owns caster switching and the portrait, so the redundant Act As
 * control is intentionally omitted. A namespaced id prefix prevents collisions
 * with the hidden legacy GM pane while both renderers coexist during the sheet migration. */
export function renderActionBarCreatureGmControls(casterId: string, casterSnapshot?: CachedCaster): string {
    const caster = casterSnapshot
        ?? (window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined);
    if (!caster) return '';
    const ecId = str(ecArrOf(caster).encounter_creature_id);
    if (ecId === '' || !(hasPlayerPermission(caster) || hasCampaignPermission(caster))) return '';
    return renderGmControls(caster, ecId, {
        includeActAs: false,
        idPrefix: 'actionbar_',
        actionBar: true,
    });
}

function renderConsole(caster: CachedCaster): string {
    const ecId = str(ecArrOf(caster).encounter_creature_id);
    if (ecId === '') return ''; // console only renders inside an encounter

    let html = '<hr />';
    if (hasPlayerPermission(caster) || hasCampaignPermission(caster)) {
        html += renderGmControls(caster, ecId);
    }
    html += '<hr />';
    html += renderStatusSection(caster, ecId);
    return html;
}

/**
 * Resolve a console caster from casterCache for an island mount, HEALING a permissions-stripped row.
 *
 * fetchAndCacheCaster's heal-on-access (unified-viewer-scoping Chunk 2) only fires on a cache MISS.
 * But a creature whose row is already cached — seeded from the encounter blob on card-open, OR
 * replaced in place by a viewer-agnostic WS broadcast that STRIPPED `permissions`
 * (the legacy full-row broadcast) — sits in the cache WITHOUT `permissions` and never
 * gets that heal. The GM islands gate on `caster.permissions`, so such a row fail-closes to a BLANK GM
 * strip / blank roster buttons even for the real GM. This reproduced on a DEAD creature: its death
 * broadcast replaced the row, and if the prior value couldn't be carried forward the row is left
 * permission-less — the card's other panes (which don't check permissions client-side) still render, so
 * only the GM controls go blank. Treat an absent-`permissions` cached row as a miss too and re-run the
 * (deduped) fetch, so get_caster_arr — which is per-viewer-correct, NOT stripped — stamps the right
 * value before we render. A genuine non-GM viewer is seeded an all-false permissions OBJECT (non-null),
 * so this never re-fetches for them. [[unified-viewer-scoping]]
 */
function ftConsoleCasterPermissionsAbsent(caster: CachedCaster | undefined): boolean {
    const p = (caster as { permissions?: unknown } | undefined)?.permissions;
    return p === undefined || p === null;
}

async function resolveConsoleCaster(casterId: string): Promise<CachedCaster | undefined> {
    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster || ftConsoleCasterPermissionsAbsent(caster)) {
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }
    return caster;
}

/**
 * Inline entry point. Renders the GM-controls + status section of the console into
 * #targetId from the client cache (fetch-on-miss). The pickpocket/trade/train block
 * stays PHP (rendered separately, after this island).
 */
export async function renderCreatureConsoleInline(casterId: string, targetId: string): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;
    const caster = await resolveConsoleCaster(casterId);
    // An unchanged console keeps its nodes and the runes already mounted in them.
    if (!paintMarkup(target, caster ? renderConsole(caster) : '') || !caster) return;
    // Hydrate any non-standard status runes (renderStatusSection emits .ft-rune-mount placeholders).
    // Stamp the caster ROOT catalog on the engine first so name1() resolves reference layers; the footer
    // scanner keeps it for blobs that ship no catalog of their own.
    try {
        const cat = caster.catalog;
        if (cat && Object.keys(cat).length) window.RuneIcon?.setCatalog?.(cat);
    } catch { /* engine not ready */ }
    window.ftMountRuneIcons?.(target);
}

/**
 * GM-controls-only mount for the console "GM" tab (encounter creatures, campaign-GM only).
 * Same fetch-on-miss path as renderCreatureConsoleInline, but renders ONLY the GM action strip
 * (renderGmControls) — no status section. Self-gates: empty if the caster isn't in an encounter
 * or the viewer lacks player/campaign permission (the PHP tab is already permission-gated; this
 * is defense-in-depth so the island never leaks GM controls into a non-GM view).
 */
export async function renderCreatureGmControlsInline(casterId: string, targetId: string): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;
    const caster = await resolveConsoleCaster(casterId);
    if (!caster) { paintMarkup(target, ''); return; }
    const ecId = str(ecArrOf(caster).encounter_creature_id);
    if (ecId === '' || !(hasPlayerPermission(caster) || hasCampaignPermission(caster))) {
        paintMarkup(target, '');
        return;
    }
    paintMarkup(target, renderGmControls(caster, ecId));
}

/**
 * Status-effects mount for the console "Status Effects" tab (encounter creatures, ALL viewers).
 * Restores the status panel that historically sat near the top of the encounter-creature card
 * (the retired creature_console_inc status section): the applied-status icon row plus the
 * show/manage button — player/campaign permission opens the modify-status form (add / edit /
 * delete / private toggle / durations) in the sidelog, a plain viewer gets the read-only
 * showStatusEffects side-info. Private statuses are filtered out for unpermitted viewers inside
 * renderStatusSection, so this tab is safe to show players. Encounter creatures only — the panel
 * is meaningless for a static library statblock (no encounter_id), so it renders empty there.
 */
export async function renderCreatureStatusEffectsInline(casterId: string, targetId: string): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;
    const caster = await resolveConsoleCaster(casterId);
    if (!caster) { paintMarkup(target, ''); return; }
    const ecId = str(ecArrOf(caster).encounter_creature_id);
    if (ecId === '') { paintMarkup(target, ''); return; } // encounter creatures only
    // An unchanged panel keeps its nodes and the runes already mounted in them.
    if (!paintMarkup(target, renderStatusSection(caster, ecId))) return;
    // Hydrate any non-standard status runes (same tail as renderCreatureConsoleInline): stamp the
    // caster ROOT catalog on the engine so name1() resolves reference layers, then mount.
    try {
        const cat = caster.catalog;
        if (cat && Object.keys(cat).length) window.RuneIcon?.setCatalog?.(cat);
    } catch { /* engine not ready */ }
    window.ftMountRuneIcons?.(target);
}
